/**
 * Part Need Reserve Dialog (#725)
 *
 * Reserves stock for a single parts-needs-cockpit row by calling the
 * existing stock_reservations lifecycle (POST /work-orders/:id/part-reservations),
 * previously only reachable from a mocked, never-wired dialog on the work
 * order detail page.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, TextField, Typography, Alert } from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';

interface PartNeedReserveDialogProps {
  open: boolean;
  onClose: () => void;
  partName: string;
  neededQuantity: number;
  currentStock: number | null;
  onReserve: (quantity: number) => void;
  isLoading?: boolean;
}

export function PartNeedReserveDialog({
  open,
  onClose,
  partName,
  neededQuantity,
  currentStock,
  onReserve,
  isLoading,
}: PartNeedReserveDialogProps) {
  const { t } = useTranslation('inventory');
  const { isDirty, markDirty } = useFormDirty(open);
  const [quantity, setQuantity] = useState(neededQuantity);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setQuantity(neededQuantity);
      setError('');
    }
  }, [open, neededQuantity]);

  const handleSubmit = () => {
    if (quantity < 1) {
      setError(t('partsDashboard.reserve.quantityMustBePositive'));
      return;
    }
    if (currentStock !== null && quantity > currentStock) {
      setError(t('partsDashboard.reserve.quantityExceedsStock'));
      return;
    }
    onReserve(quantity);
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('partsDashboard.reserve.title')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      submitLabel={t('partsDashboard.reserve.confirm')}
      isLoading={isLoading}
      maxWidth="xs"
    >
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('partsDashboard.reserve.description', { partName })}
        </Typography>
        {currentStock !== null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('partsDashboard.reserve.currentStockHint', { count: currentStock })}
          </Alert>
        )}
        <TextField
          label={t('partsDashboard.reserve.quantityLabel')}
          type="number"
          value={quantity}
          onChange={(e) => {
            setQuantity(parseInt(e.target.value, 10) || 0);
            markDirty();
            if (error) setError('');
          }}
          fullWidth
          autoFocus
          error={!!error}
          helperText={error}
          slotProps={{ htmlInput: { min: 1 } }}
        />
      </DialogContent>
    </FormDialog>
  );
}
